import { useTranslation } from 'react-i18next';
import { BuildingStorefrontIcon, MagnifyingGlassIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import FacilitySwitcher from './FacilitySwitcher';
import { useSuperAdminMode } from './navigation/useSuperAdminMode';

interface SidebarContextBlockProps {
    isCompactMode: boolean;
    onOpenFacilitySwitcher: () => void;
    onOpenHotelSearch: () => void;
    /** Tiroir mobile : le referme avant de naviguer. */
    onNavigate?: () => void;
}

/**
 * Haut de la barre latérale : où suis-je, et sur quoi.
 *
 * - Pour l'équipe, une bascule toujours visible « Vue client / Back-office » :
 *   elle dit aussi dans quel mode on est (avant, la coquille était identique
 *   dans les deux et la bascule enfouie dans le menu du compte).
 * - En back-office, le sélecteur d'établissement laisse la place à la recherche
 *   d'hôtel : changer d'hôtel n'y sert à rien et remontait la page.
 */
export default function SidebarContextBlock({ isCompactMode, onOpenFacilitySwitcher, onOpenHotelSearch, onNavigate }: SidebarContextBlockProps) {
    const { t } = useTranslation('navigation');
    const { isEligible, isOn, setOn, canSearchHotels: showHotelSearch } = useSuperAdminMode();

    const switchMode = (on: boolean) => {
        if (on === isOn) return;
        onNavigate?.();
        setOn(on);
    };

    // Fragment sans marge propre : pour un hôtelier, rien n'est ajouté à la
    // barre (le sélecteur ne s'affiche que pour un groupe ou un super admin).
    return (
        <>
            {isEligible && <div className="px-2 pt-3">{isCompactMode ? (
                <button
                    type="button"
                    onClick={() => switchMode(!isOn)}
                    title={isOn ? t('staff_shell.mode_staff') : t('staff_shell.mode_client')}
                    aria-label={isOn ? t('staff_shell.mode_staff') : t('staff_shell.mode_client')}
                    aria-pressed={isOn}
                    className={`flex justify-center rounded-xl px-3 py-2.5 transition-colors ${
                        isOn ? 'bg-main-color text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                >
                    {isOn ? <ShieldCheckIcon className="h-5 w-5" /> : <BuildingStorefrontIcon className="h-5 w-5" />}
                </button>
            ) : (
                <div role="group" aria-label={t('staff_shell.mode_label')} className="grid grid-cols-2 gap-0.5 rounded-xl bg-gray-100 p-0.5 text-xs font-medium">
                    {[false, true].map((on) => (
                        <button
                            key={String(on)}
                            type="button"
                            onClick={() => switchMode(on)}
                            aria-pressed={isOn === on}
                            className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 transition-colors ${
                                isOn === on
                                    ? on ? 'bg-main-color text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-800'
                            }`}
                        >
                            {on ? <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" /> : <BuildingStorefrontIcon className="h-4 w-4" aria-hidden="true" />}
                            {on ? t('staff_shell.mode_staff') : t('staff_shell.mode_client')}
                        </button>
                    ))}
                </div>
            )}</div>}

            {showHotelSearch ? (
                <div className="px-2 py-3">
                <button
                    type="button"
                    onClick={() => { onNavigate?.(); onOpenHotelSearch(); }}
                    title={isCompactMode ? t('staff_shell.search_trigger') : undefined}
                    aria-label={t('staff_shell.search_trigger')}
                    className={`flex items-center gap-3 rounded-xl bg-gray-100 px-3 py-2.5 text-sm text-gray-500 transition-colors hover:bg-gray-200 ${
                        isCompactMode ? 'justify-center' : ''
                    }`}
                >
                    <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    {!isCompactMode && (
                        <>
                            <span className="flex-1 truncate text-left">{t('staff_shell.search_trigger')}</span>
                            <kbd className="hidden rounded border border-gray-300 bg-white px-1.5 text-[10px] font-medium text-gray-500 lg:inline">⌘K</kbd>
                        </>
                    )}
                </button>
                </div>
            ) : (
                <FacilitySwitcher isCompactMode={isCompactMode} onClick={() => { onNavigate?.(); onOpenFacilitySwitcher(); }} />
            )}
        </>
    );
}
